import { supabase } from '@/lib/supabase'

export interface MediaCategory {
  id: string
  church_id: string
  name: string
  description?: string
  icon?: string
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface MediaItem {
  id: string
  church_id: string
  category_id?: string
  title: string
  description?: string
  media_type: 'video' | 'audio' | 'document' | 'image' | 'pdf'
  file_url: string
  file_size?: number
  file_name?: string
  mime_type?: string
  duration?: number
  speaker?: string
  sermon_date?: string
  sermon_text?: string
  service_id?: string
  tags?: string[]
  thumbnail_url?: string
  view_count: number
  download_count: number
  is_public: boolean
  is_featured: boolean
  published_at?: string
  status: 'draft' | 'published' | 'archived'
  created_at: string
  updated_at: string
  created_by?: string
  category?: MediaCategory
}

export interface MediaPlaylist {
  id: string
  church_id: string
  name: string
  description?: string
  thumbnail_url?: string
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
}

export interface UploadProgress {
  progress: number
  loaded: number
  total: number
}

class MediaService {
  // Get all categories for a church
  async getCategories(churchId: string): Promise<MediaCategory[]> {
    const { data, error } = await supabase
      .from('media_categories')
      .select('*')
      .eq('church_id', churchId)
      .eq('is_active', true)
      .order('display_order', { ascending: true })

    if (error) throw error
    return data || []
  }

  // Create a new category
  async createCategory(category: Partial<MediaCategory>): Promise<MediaCategory> {
    const { data, error } = await supabase
      .from('media_categories')
      .insert(category)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update a category
  async updateCategory(id: string, updates: Partial<MediaCategory>): Promise<MediaCategory> {
    const { data, error } = await supabase
      .from('media_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Get all media items for a church
  async getMediaItems(churchId: string, filters?: {
    category_id?: string
    media_type?: string
    status?: string
    search?: string
  }): Promise<MediaItem[]> {
    let query = supabase
      .from('media_items')
      .select(`
        *,
        category:media_categories(*)
      `)
      .eq('church_id', churchId)
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })

    if (filters?.category_id) {
      query = query.eq('category_id', filters.category_id)
    }

    if (filters?.media_type) {
      query = query.eq('media_type', filters.media_type)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,speaker.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  // Get a single media item
  async getMediaItem(id: string): Promise<MediaItem> {
    const { data, error } = await supabase
      .from('media_items')
      .select(`
        *,
        category:media_categories(*)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  }

  // Create a new media item
  async createMediaItem(item: Partial<MediaItem>): Promise<MediaItem> {
    const { data, error } = await supabase
      .from('media_items')
      .insert(item)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update a media item
  async updateMediaItem(id: string, updates: Partial<MediaItem>): Promise<MediaItem> {
    const { data, error } = await supabase
      .from('media_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete a media item
  async deleteMediaItem(id: string): Promise<void> {
    const { error } = await supabase
      .from('media_items')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Upload a file to Supabase storage
  async uploadFile(
    file: File,
    churchId: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<{ url: string; path: string }> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${churchId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

    // Upload file
    const { data, error } = await supabase.storage
      .from('church-media')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) throw error

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('church-media')
      .getPublicUrl(data.path)

    return {
      url: urlData.publicUrl,
      path: data.path
    }
  }

  // Upload thumbnail
  async uploadThumbnail(
    file: File,
    churchId: string
  ): Promise<{ url: string; path: string }> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${churchId}/thumbnails/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

    const { data, error } = await supabase.storage
      .from('church-media')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) throw error

    const { data: urlData } = supabase.storage
      .from('church-media')
      .getPublicUrl(data.path)

    return {
      url: urlData.publicUrl,
      path: data.path
    }
  }

  // Delete a file from storage
  async deleteFile(path: string): Promise<void> {
    const { error } = await supabase.storage
      .from('church-media')
      .remove([path])

    if (error) throw error
  }

  // Get featured media
  async getFeaturedMedia(churchId: string, limit: number = 5): Promise<MediaItem[]> {
    const { data, error } = await supabase
      .from('media_items')
      .select(`
        *,
        category:media_categories(*)
      `)
      .eq('church_id', churchId)
      .eq('status', 'published')
      .eq('is_featured', true)
      .order('published_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // Get popular media
  async getPopularMedia(churchId: string, limit: number = 10): Promise<MediaItem[]> {
    const { data, error } = await supabase
      .from('media_items')
      .select(`
        *,
        category:media_categories(*)
      `)
      .eq('church_id', churchId)
      .eq('status', 'published')
      .order('view_count', { ascending: false })
      .order('published_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // Get recent media
  async getRecentMedia(churchId: string, limit: number = 10): Promise<MediaItem[]> {
    const { data, error } = await supabase
      .from('media_items')
      .select(`
        *,
        category:media_categories(*)
      `)
      .eq('church_id', churchId)
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // Get media statistics
  async getMediaStats(churchId: string): Promise<{
    total_items: number
    total_views: number
    by_type: Record<string, number>
    by_category: Record<string, number>
  }> {
    const { data: items, error } = await supabase
      .from('media_items')
      .select('media_type, category_id, view_count')
      .eq('church_id', churchId)

    if (error) throw error

    const stats = {
      total_items: items?.length || 0,
      total_views: items?.reduce((sum, item) => sum + (item.view_count || 0), 0) || 0,
      by_type: {} as Record<string, number>,
      by_category: {} as Record<string, number>
    }

    items?.forEach(item => {
      stats.by_type[item.media_type] = (stats.by_type[item.media_type] || 0) + 1
      if (item.category_id) {
        stats.by_category[item.category_id] = (stats.by_category[item.category_id] || 0) + 1
      }
    })

    return stats
  }
}

export const mediaService = new MediaService()
